import {
  LocationStatusFacet,
  useCreateLocationStatusFacetMutation,
  useDeleteLocationStatusFacetMutation,
  useUpdateLocationStatusFacetMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseLocationStatusFacetMutationProps = {}

export type UseLocationStatusFacetMutationResult = {
  createLocationStatusFacet: (loc: Partial<LocationStatusFacet>, onSuccess: ((loc: LocationStatusFacet) => void) | undefined) => void
  updateLocationStatusFacet: (loc: Partial<LocationStatusFacet>, onSuccess: (() => void) | undefined) => void
  deleteLocationStatusFacet: (locId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useLocationStatusFacetMutation = (props: UseLocationStatusFacetMutationProps = {}): UseLocationStatusFacetMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createLocationStatusFacet, {isLoading: isCreating}] = useCreateLocationStatusFacetMutation()
  const [_updateLocationStatusFacet, {isLoading: isUpdating}] = useUpdateLocationStatusFacetMutation()
  const [_deleteLocationStatusFacet, {isLoading: isDeleting}] = useDeleteLocationStatusFacetMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useLocationStatusFacetUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.locationstatusfacet", "Location Status"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useLocationStatusFacetUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useLocationStatusFacetUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.locationstatusfacet", "Location Status"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useLocationStatusFacetUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useLocationStatusFacetUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.locationstatusfacet", "Location Status"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useLocationStatusFacetUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<LocationStatusFacet>, onSuccess: ((loc: LocationStatusFacet) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.locationstatusfacet", "Location Status") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.locationstatusfacet", "Location Status") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.locationstatusfacet", "Location Status") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((locId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteLocationStatusFacet({id: locId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteLocationStatusFacet, handleDeleteResult])

  const createLocationStatusFacet = useCallback((loc: Partial<LocationStatusFacet>, onSuccess: ((loc: LocationStatusFacet) => void) | undefined) => {
    const promise = _createLocationStatusFacet(loc).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createLocationStatusFacet, handleCreateResult])

  const updateLocationStatusFacet = useCallback((loc: Partial<LocationStatusFacet>, onSuccess: (() => void) | undefined) => {
    const promise = _updateLocationStatusFacet(loc).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateLocationStatusFacet, handleUpdateResult])

  const deleteLocationStatusFacet = useCallback((locId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.locationstatusfacet", "Location Status"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletelocationstatusfacet.confirm", "Are you sure you want to delete this location status?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(locId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createLocationStatusFacet: createLocationStatusFacet,
    updateLocationStatusFacet: updateLocationStatusFacet,
    deleteLocationStatusFacet: deleteLocationStatusFacet,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
